import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { crudService } from "services/crud.service";
import { initialPermissionState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { NotificationManager } from "react-notifications";
import { endPoints } from "utils/constants";

const { userPermissionEndPoint } = endPoints;

// it's for login user
export const fetchUserPermissions = createAsyncThunk(
  "permissions/fetchAllUserPermissions",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      const paginatedUrl = getPaginatedUrlWithSearchParams({
        pageNumber: 1,

        endpoint: userPermissionEndPoint,
        searchParams: {
          user__id: userId,
        },
      });
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// it's for any user from list view for detail page
export const fetchUserPermissionsForDetailPage = createAsyncThunk(
  "permissions/fetchUserPermissionsForDetailPage",
  async ({ userId }: { userId: string }, { rejectWithValue }) => {
    try {
      const paginatedUrl = getPaginatedUrlWithSearchParams({
        pageNumber: 1,

        endpoint: userPermissionEndPoint,
        searchParams: {
          user__id: userId,
        },
      });
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserPermission = createAsyncThunk(
  "permissions/updateUserPermission",
  async ({ updatedPermission, permissionId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        userPermissionEndPoint,
        permissionId,
        updatedPermission
      );
      if (response.status === 204) {
        NotificationManager.success("Permissions updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const userPermissionSlice = createSlice({
  name: "permissions",
  initialState: initialPermissionState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch user permissions
    builder
      .addCase(fetchUserPermissions.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserPermissions.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.permissions = action.payload?.objects;
        }
      )
      .addCase(
        fetchUserPermissions.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.permissions = [];
        }
      );

    //   update user permissions
    builder
      .addCase(updateUserPermission.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateUserPermission.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateUserPermission.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch user permissions for detail page
    builder
      .addCase(fetchUserPermissionsForDetailPage.pending, (state: any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserPermissionsForDetailPage.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.detailPagePermissions = action.payload?.objects;
        }
      )
      .addCase(
        fetchUserPermissionsForDetailPage.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.detailPagePermissions = [];
        }
      );
  },
});

export const userPermissionReducer = userPermissionSlice.reducer;
